import React, { useState, useMemo, useEffect } from 'react';
import './Guide.css';
import { useSite } from '../context/SiteContext';
import { useT, useI18n } from '../i18n';
import LangSwitcher from '../components/LangSwitcher';

// 优先用管理员在后台配置的 public_url,未配置则 fallback 到当前浏览器地址
const fallbackOrigin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';

// 工具列表
//   protocol: 该工具走哪种协议 —— 'anthropic' 走 /v1/messages,'openai' 走 /v1/chat/completions
//   recommended: 是否打「推荐」角标(用显式字段,不要靠 badge 文案里有没有「推荐」两个字 —— 英文界面就失效了)
const TOOLS = [
  { id: 'cc-switch',   name: 'CC Switch',         protocol: 'anthropic', recommended: true, badgeKey: 'guide.tool.ccswitch.badge' },
  { id: 'claude-code', name: 'Claude Code',       protocol: 'anthropic', badgeKey: 'guide.tool.claudecode.badge' },
  { id: 'openclaw',    name: 'OpenClaw',          protocol: 'anthropic', badgeKey: 'guide.tool.openclaw.badge' },
  { id: 'opencode',    name: 'OpenCode',          protocol: 'openai',    badgeKey: 'guide.tool.opencode.badge' },
  { id: 'openai',      nameKey: '指南.OpenAI客户端', protocol: 'openai',  badgeKey: 'guide.tool.openai.badge' },
  { id: 'curl',        nameKey: '指南.Curl',       protocol: 'openai',    badgeKey: 'guide.tool.curl.badge' },
];

const OSES = [
  { id: 'windows', name: '🪟 Windows' },
  { id: 'mac',     name: 'macOS' },
  { id: 'linux',   name: '🐧 Linux' },
];

// 生成接入配置
//
// 协议是这里最关键的一条分界线:
//   - Anthropic 协议(Claude Code / CC Switch / OpenClaw)请求打到 <origin>/v1/messages,
//     客户端(Claude Code 等)会自己在 base URL 后面拼 /v1/messages,
//     所以 base URL 只能给到站点根,再多写一个 /v1 就变成 /v1/v1/messages 直接 404。
//   - OpenAI 协议(OpenCode / 通用客户端 / cURL)请求打到 <origin>/v1/chat/completions,
//     base URL 必须是 <origin>/v1。
// 之前这两个协议都套同一套模板,给 Anthropic 客户端发了带 /v1 的地址,是错的。
//
// 另外注意:origin 必须由调用方传进来。之前 buildConfig 内部直接引用了一个未定义的
// origin,实际落到了浏览器全局的 window.origin 上 —— 后台配置的 public_url 完全没被用上,
// 本地调试时就会给用户生成 http://localhost:3003/v1 这种地址。
//
// 不生成 ANTHROPIC_CONTEXT_LENGTH:上下文窗口是网关内部的计费依据(渠道里配),
// 不是给客户端的承诺 —— 网关不去限制客户能用多大,客户想调多大自己改客户端配置,
// 超出门槛的部分按溢价计费即可。主动塞一个值反而会让客户端提前压缩,
// 也会和客户自己配的值打架。
function buildConfig({ tool, os, model, key, lang, origin }) {
  const en = lang === 'en-US';
  const c = (zh, enText) => (en ? enText : zh);

  const placeholderKey = en ? '<YOUR_API_KEY>' : '<你的 API Key>';
  const placeholderModel = en ? '<model>' : '<模型>';
  const k = key || placeholderKey;
  const m = model || placeholderModel;

  // 站点根(Anthropic 协议用)与 OpenAI 兼容根(带 /v1)
  const root = (origin || '').replace(/\/+$/, '');
  const oaiBase = root + '/v1';

  // 环境变量赋值:Windows 用 PowerShell 语法,其余用 shell export
  const envLines = (pairs) => (os === 'windows'
    ? pairs.map(([kk, vv]) => `$env:${kk}="${vv}"`)
    : pairs.map(([kk, vv]) => `export ${kk}="${vv}"`)
  ).join('\n');

  switch (tool) {
    // ---------- Anthropic 协议 ----------
    case 'claude-code':
      return [
        c('# Claude Code 配置(Anthropic 兼容协议)', '# Claude Code config (Anthropic compatible)'),
        c('# Base URL 不要加 /v1 —— Claude Code 会自己拼 /v1/messages', '# Do NOT add /v1 to the base URL - Claude Code appends /v1/messages itself'),
        envLines([
          ['ANTHROPIC_BASE_URL', root],
          ['ANTHROPIC_AUTH_TOKEN', k],
          ['ANTHROPIC_MODEL', m],
        ]),
        '',
        c(`# 长期生效请写入 ${os === 'windows' ? '%USERPROFILE%\\.claude\\settings.json' : '~/.claude/settings.json'} 的 env 字段`,
          `# To make it permanent, put the same values in the env block of ${os === 'windows' ? '%USERPROFILE%\\.claude\\settings.json' : '~/.claude/settings.json'}`),
      ].join('\n');

    case 'cc-switch':
      // CC Switch 管的就是 Claude Code 的 settings.json,直接给这份 JSON
      return [
        c('# CC Switch 配置(Anthropic 兼容协议)', '# CC Switch config (Anthropic compatible)'),
        c('# 在 CC Switch 里新增一个 Claude Code 供应商,填入下面的内容', '# Add a Claude Code provider in CC Switch with the values below'),
        '{',
        '  "env": {',
        `    "ANTHROPIC_BASE_URL": "${root}",`,
        `    "ANTHROPIC_AUTH_TOKEN": "${k}",`,
        `    "ANTHROPIC_MODEL": "${m}"`,
        '  }',
        '}',
        '',
        c('# API 地址填站点根,不要加 /v1', '# Use the site root as the API URL, without /v1'),
      ].join('\n');

    case 'openclaw':
      // OpenClaw 走自定义 provider,这样才能指定我们的模型名
      return [
        c('# OpenClaw 配置(Anthropic 兼容协议)', '# OpenClaw config (Anthropic compatible)'),
        c('# 写入 ~/.openclaw/openclaw.json 的自定义供应商:', '# Add a custom provider in ~/.openclaw/openclaw.json:'),
        '{',
        '  "models": {',
        '    "providers": {',
        '      "meowapi": {',
        `        "baseUrl": "${root}",`,
        `        "apiKey": "${k}",`,
        '        "models": [',
        `          { "id": "${m}", "name": "${m}", "api": "anthropic-messages", "input": ["text"] }`,
        '        ]',
        '      }',
        '    }',
        '  }',
        '}',
        '',
        c('# 也可以改用环境变量:ANTHROPIC_BASE_URL / ANTHROPIC_API_KEY',
          '# Alternatively use env vars: ANTHROPIC_BASE_URL / ANTHROPIC_API_KEY'),
      ].join('\n');

    // ---------- OpenAI 协议 ----------
    case 'opencode':
      return [
        c('# OpenCode 配置(OpenAI 兼容协议)', '# OpenCode config (OpenAI compatible)'),
        c(`# 写入 ${os === 'windows' ? '%USERPROFILE%\\.config\\opencode\\opencode.json' : '~/.config/opencode/opencode.json'}(或项目根目录的 opencode.json):`,
          `# Write to ${os === 'windows' ? '%USERPROFILE%\\.config\\opencode\\opencode.json' : '~/.config/opencode/opencode.json'} (or opencode.json in the project root):`),
        '{',
        '  "$schema": "https://opencode.ai/config.json",',
        '  "provider": {',
        '    "meowapi": {',
        '      "npm": "@ai-sdk/openai-compatible",',
        '      "name": "MeowApi",',
        '      "options": {',
        `        "baseURL": "${oaiBase}",`,
        `        "apiKey": "${k}"`,
        '      },',
        '      "models": {',
        `        "${m}": { "name": "${m}" }`,
        '      }',
        '    }',
        '  }',
        '}',
      ].join('\n');

    case 'openai':
      return [
        c('# OpenAI 兼容客户端配置(Cherry Studio / Cursor / Python SDK 等)',
          '# OpenAI compatible client config (Cherry Studio / Cursor / Python SDK, etc.)'),
        c('# Base URL 结尾的 /v1 要保留 —— OpenAI 协议是 <站点根>/v1/chat/completions',
          '# Keep the trailing /v1 - OpenAI compatible endpoints live at <site root>/v1/chat/completions'),
        envLines([
          ['OPENAI_BASE_URL', oaiBase],
          ['OPENAI_API_KEY', k],
          ['OPENAI_MODEL', m],
        ]),
        '',
        c('# Python 示例:', '# Python example:'),
        '# from openai import OpenAI',
        c(`# client = OpenAI(api_key="${k}", base_url="${oaiBase}")`, `# client = OpenAI(api_key="${k}", base_url="${oaiBase}")`),
        c(`# client.chat.completions.create(model="${m}", messages=[{"role":"user","content":"hello"}])`,
          `# client.chat.completions.create(model="${m}", messages=[{"role":"user","content":"hello"}])`),
      ].join('\n');

    case 'curl':
      return [
        c('# 测试 API 是否可用', '# Test if the API is available'),
        `curl -X POST "${oaiBase}/chat/completions" \\`,
        '  -H "Content-Type: application/json" \\',
        `  -H "Authorization: Bearer ${k}" \\`,
        `  -d '{"model":"${m}","messages":[{"role":"user","content":"hello"}]}'`,
      ].join('\n');

    default:
      return [
        c('# 接入配置', '# Integration config'),
        `Base URL: ${oaiBase}`,
        `API Key: ${k}`,
        `Model: ${m}`,
      ].join('\n');
  }
}

const Guide = () => {
  const { site } = useSite();
  const t = useT();
  const { lang } = useI18n();
  const [step, setStep] = useState(1);
  const [apiKey, setApiKey] = useState('');
  const [tool, setTool] = useState('cc-switch');
  const [os, setOs] = useState('windows');
  // 模型列表从后端拉(渠道里实际启用的模型),
  // 不再前端硬编码 —— 硬编码的列表会随渠道调整而失效(新增模型选不到、模型下线了还在)
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [copied, setCopied] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');

  useEffect(() => {
    fetch('/api/status')
      .then((r) => r.json())
      .then((j) => {
        const u = (j && j.data && j.data.public_url) || '';
        setPublicUrl(u.replace(/\/+$/, ''));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/models')
      .then((r) => r.json())
      .then((j) => {
        const list = (j && j.data) || [];
        setModels(list);
        // 默认选第一个;已选的还在列表里就保持不变
        setModel((prev) => (prev && list.some((m) => m.model === prev) ? prev : (list[0] && list[0].model) || ''));
      })
      .catch(() => {});
  }, []);

  const origin = publicUrl || fallbackOrigin;

  const code = useMemo(
    () => buildConfig({ tool, os, model, key: apiKey, lang, origin }),
    [tool, os, model, apiKey, lang, origin]
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const canProceed = {
    1: apiKey.trim().length >= 5,
    2: !!tool,
    3: !!os,
    4: !!model,
    5: true,
  };

  const steps = [
    { n: 1, title: t('指南.步骤1标题'), desc: t('指南.步骤1描述') },
    { n: 2, title: t('指南.步骤2标题'), desc: t('指南.步骤2描述') },
    { n: 3, title: t('指南.步骤3标题'), desc: t('指南.步骤3描述') },
    { n: 4, title: t('指南.步骤4标题'), desc: t('指南.步骤4描述') },
    { n: 5, title: t('指南.步骤5标题'), desc: t('指南.步骤5描述') },
  ];

  const curTool = TOOLS.find((it) => it.id === tool);

  return (
    <div className="guide-page">
      {/* 顶部导航(与主页一致: logo + site_name · 接入指南) */}
      <header className="navbar">
        <div className="navbar__inner">
          <a href="/" className="navbar__brand">
            <img src="/logo.svg" alt="" className="navbar__logo" />
            <span className="navbar__title">{site.site_name} · {t('导航.接入指南')}</span>
          </a>
          <div className="navbar__actions">
            <LangSwitcher variant="navbar" />
          </div>
        </div>
      </header>

      <main className="guide-main">
        <div className="guide-important">
          <div className="guide-important__title">{t('指南.重要提示')}</div>
          <ul className="guide-important__list">
            <li>{t('指南.重要提示1')}</li>
            <li>{t('指南.重要提示2')}</li>
          </ul>
        </div>

        {/* 步骤条 */}
        <div className="guide-steps-bar">
          {steps.map((s) => {
            const active = step === s.n;
            const done = step > s.n;
            return (
              <div
                key={s.n}
                className={`guide-step-indicator ${active ? 'is-active' : ''} ${done ? 'is-done' : ''}`}
                onClick={() => setStep(s.n)}
              >
                <div className="guide-step-indicator__num">{done ? '✓' : s.n}</div>
                <div className="guide-step-indicator__text">{s.title}</div>
              </div>
            );
          })}
        </div>

        {/* 步骤内容 */}
        <div className="guide-step-card">
          {step === 1 && (
            <StepCard num={1} title={t('指南.步骤1标题')}>
              <p className="guide-desc">{t('指南.步骤1详细')} <code>sk-xxxxx</code></p>
              <input
                className="guide-input"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={t('指南.步骤1占位符')}
              />
            </StepCard>
          )}

          {step === 2 && (
            <StepCard num={2} title={t('指南.步骤2标题')}>
              <div className="guide-grid guide-grid--tools">
                {TOOLS.map((it) => (
                  <button
                    key={it.id}
                    className={`guide-tile ${tool === it.id ? 'is-selected' : ''}`}
                    onClick={() => setTool(it.id)}
                  >
                    {it.recommended && <span className="guide-tile__badge">{t('指南.推荐')}</span>}
                    <div className="guide-tile__name">{it.nameKey ? t(it.nameKey) : it.name}</div>
                    <div className="guide-tile__sub">{t(it.badgeKey)}</div>
                    <div className={`guide-tile__proto guide-tile__proto--${it.protocol}`}>
                      {it.protocol === 'anthropic' ? t('指南.Anthropic') : t('指南.OpenAI')}
                    </div>
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 3 && (
            <StepCard num={3} title={t('指南.步骤3标题')}>
              <div className="guide-grid guide-grid--os">
                {OSES.map((o) => (
                  <button
                    key={o.id}
                    className={`guide-tile guide-tile--compact ${os === o.id ? 'is-selected' : ''}`}
                    onClick={() => setOs(o.id)}
                  >
                    {o.name}
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 4 && (
            <StepCard num={4} title={t('指南.步骤4标题')}>
              <div className="guide-grid guide-grid--models">
                {models.length === 0 ? (
                  <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: 16, textAlign: 'center', color: '#86909c', fontSize: 12 }}>
                    {t('指南.无模型')}
                  </div>
                ) : (
                  models.map((m) => (
                    <button
                      key={m.model}
                      className={`guide-tile ${model === m.model ? 'is-selected' : ''}`}
                      onClick={() => setModel(m.model)}
                    >
                      <div className="guide-tile__name">{m.model}</div>
                    </button>
                  ))
                )}
              </div>
            </StepCard>
          )}

          {step === 5 && (
            <StepCard num={5} title={t('指南.步骤5标题')}>
              <div className="guide-code-wrap">
                <div className="guide-code-header">
                  <span className="guide-code-title">
                    {curTool ? (curTool.nameKey ? t(curTool.nameKey) : curTool.name) : t('指南.配置')}
                    {curTool ? ` · ${curTool.protocol === 'anthropic' ? t('指南.Anthropic') : t('指南.OpenAI')}` : ''}
                  </span>
                  <button className="guide-btn guide-btn--ghost" onClick={copy}>
                    {copied ? t('公共.已复制') : t('公共.复制')}
                  </button>
                </div>
                <pre className="guide-code"><code>{code}</code></pre>
                <div className="guide-code-tip">{t('指南.粘贴提示')}</div>
              </div>
            </StepCard>
          )}
        </div>

        {/* 导航按钮 */}
        <div className="guide-nav">
          {step > 1 && (
            <button className="guide-btn guide-btn--ghost" onClick={() => setStep(step - 1)}>{t('公共.上一步')}</button>
          )}
          {step < 5 && canProceed[step] && (
            <button className="guide-btn guide-btn--primary" onClick={() => setStep(step + 1)}>{t('公共.下一步')}</button>
          )}
          {step < 5 && !canProceed[step] && (
            <button className="guide-btn guide-btn--primary guide-btn--disabled" disabled>{t('公共.下一步')}</button>
          )}
        </div>

        {/* 底部保障 */}
        <div className="guide-guarantee">
          <div className="guide-guarantee__header">
            <h3 className="guide-guarantee__title">{t('指南.售后保障')}</h3>
            {site.support_enabled && site.support_content && (
              <button className="guide-btn guide-btn--green" onClick={() => alert(site.support_content)}>{t('导航.联系客服')}</button>
            )}
          </div>
          <ul className="guide-guarantee__list">
            <li>{t('指南.售后保障1')}</li>
            <li>{t('指南.售后保障2')}</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

const StepCard = ({ num, title, children }) => (
  <div className="guide-step-card">
    <div className="guide-step-header">
      <span className="guide-step-num">{num}</span>
      <h2 className="guide-step-title">{title}</h2>
    </div>
    {children}
  </div>
);

export default Guide;
